import { z, uuidSchema } from '@kc3/validation';
import { baseEventFields } from './_base.js';
import { IncidentSeveritySchema } from '../enums/index.js';
/**
 * Supervisor action event (Phase 2 / extended in Phase 4 Task 8).
 *
 * Phase 2 shipped the lightweight `monitor`/`whisper`/`barge`/`coach`/
 * `force_logout` flavour for ad-hoc supervisor signals. Phase 4 Task 8
 * extends the schema to model the full live-call control surface
 * (listen/whisper/barge/transfer/park/conference/hold). Both old and new
 * action names remain valid so existing producers (force-logout etc.)
 * continue to round-trip without changes.
 *
 * Fields:
 *   - `callId` — required for live-call controls (listen/whisper/barge
 *     etc.); optional for agent-scoped actions (`force_logout`).
 *   - `targetAgentId` — present for agent-scoped supervisor signals;
 *     omitted for call-control events that target a `callId` directly.
 *   - `targetEndpoint` — populated for transfer actions (PJSIP/...).
 */
export const SupervisorActionEventSchema = z.object({
    type: z.literal('supervisor.action'),
    ...baseEventFields,
    supervisorId: uuidSchema,
    action: z.enum([
        // Phase 2 originals
        'monitor',
        'whisper',
        'barge',
        'coach',
        'force_logout',
        // Phase 4 Task 12 addition — supervisor dashboard "force pause" action.
        'force_pause',
        // Phase 4 Task 8 additions
        'listen',
        'blind_transfer',
        'attended_transfer',
        'park',
        'unpark',
        'conference',
        'hold',
        'unhold',
    ]),
    callId: uuidSchema.optional(),
    targetAgentId: uuidSchema.optional(),
    targetEndpoint: z.string().min(1).optional(),
    /** HMAC-SHA256 signature of the action payload (excluding this field). */
    sig: z.string(),
});
/**
 * Phase 4 Task 8: Failure event emitted by the worker when an executor
 * could not carry out a supervisor action (no_call, no_bridge, ARI
 * failure, …). Routed on `supervisor.action.failed.<tenantSlug>` so the
 * supervisor UI can render a notification.
 */
export const SupervisorActionFailedEventSchema = z.object({
    type: z.literal('supervisor.action.failed'),
    ...baseEventFields,
    supervisorId: uuidSchema,
    action: z.string().min(1),
    callId: uuidSchema.optional(),
    outcome: z.enum(['no_call', 'no_bridge', 'unsupported', 'failure']),
    reason: z.string().min(1),
});
export const SupervisorAlertEventSchema = z.object({
    type: z.literal('supervisor.alert'),
    ...baseEventFields,
    alertId: uuidSchema,
    severity: IncidentSeveritySchema,
    message: z.string().min(1),
    relatedAgentId: uuidSchema.optional(),
});
/**
 * Phase 2 Task 13: SIP fraud detection event.
 *
 * Emitted by the worker's SIP fraud detector when it spots a suspicious
 * outbound origination pattern (premium-rate prefix, agent burst, etc.).
 * Routed on `supervisor.alert.<tenantSlug>` so supervisors and any
 * incident-response automation can react in real time.
 *
 * `type` is `'SIP_FRAUD_SUSPECT'` rather than `'supervisor.alert'` so
 * downstream consumers can discriminate fraud-specific signals from
 * generic supervisor alerts (KPI breaches, agent abandonment, etc.).
 */
export const SipFraudSuspectEventSchema = z.object({
    type: z.literal('SIP_FRAUD_SUSPECT'),
    ...baseEventFields,
    severity: z.literal('HIGH'),
    agentId: uuidSchema,
    callId: uuidSchema,
    destination: z.string().min(1),
    reason: z.string().min(1),
    detectedAt: z.string().datetime(),
});
//# sourceMappingURL=supervisor.js.map